import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectAppConfigs } from '../../features/app-config/app-config-selector';
import BackButton from '../back-button/back-button';
import './skill-launch.scss';
import { IRootState } from '../../app/store';
import BackButtonNavigationTracking from '../../utils/back-button-helper';
import { selectIsWhitelistedIp } from '../../features/referential-access/referential-access-selector';
import {
    selectClientId,
    selectUserId,
    selectUserEmail,
    selectUserAuthenticationStatus,
} from '../../features/login/login-selector';
import { cancelledAsyncLogin } from '../../features/login/login-slice';
import {
    saveSkillView,
    getCourseDetails,
    getCourseIdsByCourseIdentifiers,
} from './skill-launch-thunk';
import {
    selectCourseAsyncStatus,
    selectCourseDetails,
    selectCourseIdByIdentifier,
    selectCourseIdByIdentifierAsyncStatus,
} from './skill-launch-selector';
import { hasSucceeded } from '../../../libs/utils/constants/async-status';
import { CONNECT_PLAYER_READY } from '../../constants/self-enrollment';

const SkillLaunch = (props: any) => {
    const { match } = props;
    const dispatch = useDispatch();
    const baseClassName = 'poc-launch-page';
    const appConfigs = useSelector(selectAppConfigs);
    const [pocLaunchTabUrl] = React.useState<string>(
        `${window.location.pathname}${window.location.hash}`
    );
    const isIpWhitelisted = useSelector(selectIsWhitelistedIp);
    const isUserAuthenticated = useSelector(selectUserAuthenticationStatus);
    const ecrId = pocLaunchTabUrl?.split('/')[1];
    const courseIdFromProps = match?.params?.id;
    const tabUrl = pocLaunchTabUrl?.split('#')[1];
    const userId = useSelector(selectUserId);
    const clientId = useSelector(selectClientId);
    const email = useSelector(selectUserEmail) || 'referential';
    const courseDetails: any = useSelector(selectCourseDetails);
    const courseDetailsAsyncStatus = useSelector(selectCourseAsyncStatus);
    const retrievedCourseId = useSelector(selectCourseIdByIdentifier);
    const [selectedCourseId, setSelectedCourseId] = useState<
        number | undefined
    >(
        !Number.isNaN(Number(courseIdFromProps))
            ? Number(courseIdFromProps)
            : undefined
    );
    const courseIdByIdentifierAsyncStatus = useSelector(
        selectCourseIdByIdentifierAsyncStatus
    );

    const courseIdByCopyLink = !Number.isNaN(
        Number(pocLaunchTabUrl?.split('/')[3].split('#')[0])
    )
        ? Number(pocLaunchTabUrl?.split('/')[3].split('#')[0])
        : retrievedCourseId;

    useEffect(() => {
        const handleConnectPlayerMessage = (event: MessageEvent) => {
            // Handle CONNECT_PLAYER_READY event from Connect Player to send parent name to Connect Player.
            if (
                event.origin === appConfigs.CONNECT_PLAYER_BASE_URL &&
                event.data?.type === CONNECT_PLAYER_READY &&
                event.source
            ) {
                (event.source as WindowProxy).postMessage(
                    { parentName: 'POC' },
                    appConfigs.CONNECT_PLAYER_BASE_URL
                );
            }
        };

        window.addEventListener('message', handleConnectPlayerMessage);

        return () => {
            window.removeEventListener('message', handleConnectPlayerMessage);
        };
    }, [
        appConfigs.CONNECT_PLAYER_BASE_URL,
        appConfigs.CONNECT_ADAPTER_BASE_URL,
    ]);

    useEffect(() => {
        const id = pocLaunchTabUrl?.split('/')[3].split('#')[0];
        if (
            Number.isNaN(Number(id)) ||
            Number.isNaN(Number(courseIdFromProps))
        ) {
            dispatch(getCourseIdsByCourseIdentifiers(id));
        }
    }, [pocLaunchTabUrl, courseIdFromProps]);

    useEffect(() => {
        if (
            Number.isNaN(Number(courseIdFromProps)) &&
            retrievedCourseId &&
            hasSucceeded(courseIdByIdentifierAsyncStatus)
        ) {
            setSelectedCourseId(Number(retrievedCourseId));
            window.history.replaceState(
                {},
                '',
                `${window.location.pathname.replace(
                    /\/[^/]+\/?$/,
                    `/${retrievedCourseId}/`
                )}`
            );
            sessionStorage.setItem('courseId', String(retrievedCourseId));
        }
    }, [retrievedCourseId, courseIdFromProps, courseIdByIdentifierAsyncStatus]);

    useEffect(() => {
        if (!(localStorage.getItem('IS_USER_AUTHENTICATED') === 'true')) {
            dispatch(cancelledAsyncLogin());
        }
    }, []);

    let connectPlayerLaunchLink;
    if (tabUrl !== undefined) {
        // eslint-disable-next-line max-len
        connectPlayerLaunchLink = `${
            appConfigs.CONNECT_PLAYER_BASE_URL
        }/launch?courseId=${courseIdByCopyLink}&userId=${userId}&ecrId=${ecrId}&isIpWhitelisted=${
            isUserAuthenticated ? false : isIpWhitelisted
        }&hideFooter=true#${tabUrl}`;
    } else {
        // eslint-disable-next-line max-len
        connectPlayerLaunchLink = `${
            appConfigs.CONNECT_PLAYER_BASE_URL
        }/launch?courseId=${selectedCourseId}&userId=${userId}&ecrId=${ecrId}&isIpWhitelisted=${
            isUserAuthenticated ? false : isIpWhitelisted
        }&hideFooter=true`;
    }
    const historyStack = useSelector(
        (state: IRootState) => state.navigation.history
    );
    const canGoBack = historyStack.length > 1;

    useEffect(() => {
        if (selectedCourseId !== 0 && selectedCourseId) {
            dispatch(getCourseDetails(selectedCourseId));
        }
    }, [selectedCourseId]);

    useEffect(() => {
        if (hasSucceeded(courseDetailsAsyncStatus)) {
            const payload = {
                clientId,
                courseIdentifier: courseDetails?.courseIdentifier,
                viewedBy: email,
            };
            dispatch(saveSkillView(payload));
        }
    }, [courseDetailsAsyncStatus]);

    return (
        <div className={`${baseClassName}`}>
            <div id="content" tabIndex={-1} />
            {canGoBack && window.history.length > 0 && <BackButton />}
            <iframe
                title={`Skill - ${courseDetails?.name || 'Course Content'}`}
                className={`${baseClassName}__course-launch`}
                /* eslint-disable-next-line max-len */
                sandbox="allow-modals allow-forms allow-popups allow-scripts allow-same-origin allow-top-navigation allow-top-navigation-by-user-activation allow-downloads allow-popups-to-escape-sandbox"
                src={`${connectPlayerLaunchLink}`}
                allow="clipboard-read clipboard-write downloads *"
            />
        </div>
    );
};
export default BackButtonNavigationTracking('Skill Page', SkillLaunch);
